import { PetInteractionLevel } from "../../../constants/pet.constants";
import { ServiceCategory } from "../../../interfaces/earnings-setup";
import {
  AdminOrderStatus,
  AdminOrderFulfillmentStatus,
  AdminOrderPaymentMode,
  AdminOrderPaymentStatus,
  OrderPaymentStatus,
} from "../../../schemas/admin-order.schema";
import { OrderLineItemUnitStatus } from "../../../schemas/order-details.schema";
import { PaymentMethodType, PaymentProvider, RefundStatus } from "../../../schemas/order-payment.schema";
import {
  OrderLineItemFulfillmentStatus,
  OrderLineItemShippingStatus,
} from "../../../schemas/update-order-line-item.schema";
import { SPLOOT_BADGE_COLOR } from "../../../themes";
// =============================================================================
// Build order — service categories & “add service” CTAs
// =============================================================================

export const ADD_SERVICE_TEXT_BY_CATEGORY: Record<ServiceCategory, string> = {
  [ServiceCategory.WALKING]: "Add Walking Service",
  [ServiceCategory.GROOMING]: "Add Grooming Service",
  [ServiceCategory.PRODUCTS]: "Add Products",
};

export const BUILD_ORDER_SERVICE_CATEGORIES: ServiceCategory[] = [
  ServiceCategory.WALKING,
  ServiceCategory.GROOMING,
  ServiceCategory.PRODUCTS,
];

export const CREATE_ORDER_PAYMENT_MODE_OPTIONS: {
  value: AdminOrderPaymentMode;
  label: string;
}[] = [
  { value: AdminOrderPaymentMode.PREPAID, label: "Prepaid" },
  { value: AdminOrderPaymentMode.POSTPAID, label: "Postpaid" }
];

/** Cart composition profile — drives which payment modes are selectable at checkout. */
export enum OrderCartPaymentProfile {
  EMPTY = "EMPTY",
  WALKING_ONLY = "WALKING_ONLY",
  GROOMING_SUBSCRIPTION_ONLY = "GROOMING_SUBSCRIPTION_ONLY",
  GROOMING_ONE_TIME_ONLY = "GROOMING_ONE_TIME_ONLY",
  PRODUCTS_ONLY = "PRODUCTS_ONLY",
  MIXED = "MIXED",
}

export const ALLOWED_PAYMENT_MODES_BY_CART_PROFILE: Record<
  OrderCartPaymentProfile,
  AdminOrderPaymentMode[]
> = {
  [OrderCartPaymentProfile.EMPTY]: [AdminOrderPaymentMode.PREPAID],
  [OrderCartPaymentProfile.WALKING_ONLY]: [AdminOrderPaymentMode.PREPAID],
  [OrderCartPaymentProfile.GROOMING_SUBSCRIPTION_ONLY]: [AdminOrderPaymentMode.PREPAID],
  [OrderCartPaymentProfile.GROOMING_ONE_TIME_ONLY]: [
    AdminOrderPaymentMode.PREPAID,
    AdminOrderPaymentMode.BOOKING_FEE,
  ],
  [OrderCartPaymentProfile.PRODUCTS_ONLY]: [
    AdminOrderPaymentMode.PREPAID,
    AdminOrderPaymentMode.POSTPAID,
  ],
  [OrderCartPaymentProfile.MIXED]: [AdminOrderPaymentMode.PREPAID],
};


export enum WalkingComfortLevel {
  NOT_COMFORTABLE = "Not comfortable",
  UNCOMFORTABLE_SOMETIMES = "Uncomfortable sometimes",
  MOSTLY_COMFORTABLE = "Mostly comfortable",
  VERY_COMFORTABLE = "Very comfortable",
}

export const WALKING_COMFORT_OPTIONS = [
  WalkingComfortLevel.NOT_COMFORTABLE,
  WalkingComfortLevel.UNCOMFORTABLE_SOMETIMES,
  WalkingComfortLevel.MOSTLY_COMFORTABLE,
  WalkingComfortLevel.VERY_COMFORTABLE,
] as const;

const COMFORT_TO_INTERACTION: Record<WalkingComfortLevel, PetInteractionLevel> = {
  [WalkingComfortLevel.NOT_COMFORTABLE]: PetInteractionLevel.NOT_COMFORTABLE,
  [WalkingComfortLevel.UNCOMFORTABLE_SOMETIMES]: PetInteractionLevel.UNCOMFORTABLE_SOMETIMES,
  [WalkingComfortLevel.MOSTLY_COMFORTABLE]: PetInteractionLevel.MOSTLY_COMFORTABLE,
  [WalkingComfortLevel.VERY_COMFORTABLE]: PetInteractionLevel.VERY_COMFORTABLE,
};

export function toInteractionLevel(comfort: WalkingComfortLevel): PetInteractionLevel {
  return COMFORT_TO_INTERACTION[comfort];
}

const INTERACTION_TO_COMFORT = Object.fromEntries(
  Object.entries(COMFORT_TO_INTERACTION).map(([comfort, level]) => [level, comfort]),
) as Record<PetInteractionLevel, WalkingComfortLevel>;

export function fromInteractionLevel(
  level: PetInteractionLevel | undefined,
): WalkingComfortLevel | undefined {
  return level ? INTERACTION_TO_COMFORT[level] : undefined;
}

// =============================================================================
// Grooming flow — plan vs one-time
// =============================================================================

export enum GroomingFlowType {
  PLAN = "PLAN",
  ONE_TIME = "ONE_TIME",
}

// =============================================================================
// Order categories (quick-filter chips)
// =============================================================================

export enum CategoryFilterKey {
  ALL = "all",
  WALKING = "WALKING",
  GROOMING = "GROOMING",
  FOOD = "FOOD",
}

// =============================================================================
// Order details drawer — mode, status badges, filters, documents tab
// =============================================================================

export enum OrderDetailsDrawerMode {
  DETAILS = "details",
  CANCEL = "cancel",
}

export enum OrderDetailsTabId {
  ORDER_DETAILS = "order-details",
  PAYMENT_COINS = "payment-coins",
}

export const ORDER_STATUS_LABELS: Record<AdminOrderStatus, string> = {
  [AdminOrderStatus.DRAFT]: "Draft",
  [AdminOrderStatus.CONFIRMED]: "Confirmed",
  [AdminOrderStatus.CANCELLED]: "Cancelled",
};

export const ORDER_STATUS_COLORS: Record<AdminOrderStatus, SPLOOT_BADGE_COLOR> = {
  [AdminOrderStatus.DRAFT]: SPLOOT_BADGE_COLOR.GRAY,
  [AdminOrderStatus.CONFIRMED]: SPLOOT_BADGE_COLOR.BLUE,
  [AdminOrderStatus.CANCELLED]: SPLOOT_BADGE_COLOR.RED,
};

export const FULFILLMENT_STATUS_LABELS: Record<AdminOrderFulfillmentStatus, string> = {
  [AdminOrderFulfillmentStatus.UNFULFILLED]: "Unfulfilled",
  [AdminOrderFulfillmentStatus.PARTIALLY_FULFILLED]: "Partially Fulfilled",
  [AdminOrderFulfillmentStatus.FULFILLED]: "Fulfilled",
};

export const LINE_ITEM_UNIT_STATUS_LABELS: Record<OrderLineItemUnitStatus, string> = {
  [OrderLineItemUnitStatus.ACTIVE]: "Active",
  [OrderLineItemUnitStatus.CANCELLED]: "Cancelled",
};

export const LINE_ITEM_UNIT_STATUS_COLORS: Record<OrderLineItemUnitStatus, SPLOOT_BADGE_COLOR> = {
  [OrderLineItemUnitStatus.ACTIVE]: SPLOOT_BADGE_COLOR.GREEN,
  [OrderLineItemUnitStatus.CANCELLED]: SPLOOT_BADGE_COLOR.RED,
};

export const LINE_ITEM_FULFILLMENT_STATUS_LABELS: Record<OrderLineItemFulfillmentStatus, string> = {
  [OrderLineItemFulfillmentStatus.PENDING]: "Pending",
  [OrderLineItemFulfillmentStatus.IN_PROGRESS]: "In Progress",
  [OrderLineItemFulfillmentStatus.PARTIALLY_FULFILLED]: "Partially Fulfilled",
  [OrderLineItemFulfillmentStatus.FULFILLED]: "Fulfilled",
  [OrderLineItemFulfillmentStatus.FAILED]: "Failed",
};

export const LINE_ITEM_FULFILLMENT_STATUS_COLORS: Record<
  OrderLineItemFulfillmentStatus,
  SPLOOT_BADGE_COLOR
> = {
  [OrderLineItemFulfillmentStatus.PENDING]: SPLOOT_BADGE_COLOR.ORANGE,
  [OrderLineItemFulfillmentStatus.IN_PROGRESS]: SPLOOT_BADGE_COLOR.BLUE,
  [OrderLineItemFulfillmentStatus.PARTIALLY_FULFILLED]: SPLOOT_BADGE_COLOR.YELLOW,
  [OrderLineItemFulfillmentStatus.FULFILLED]: SPLOOT_BADGE_COLOR.GREEN,
  [OrderLineItemFulfillmentStatus.FAILED]: SPLOOT_BADGE_COLOR.RED,
};

export const LINE_ITEM_SHIPPING_STATUS_LABELS: Record<OrderLineItemShippingStatus, string> = {
  [OrderLineItemShippingStatus.PENDING]: "Pending",
  [OrderLineItemShippingStatus.SHIPPED]: "Shipped",
  [OrderLineItemShippingStatus.DELIVERED]: "Delivered",
};

export const LINE_ITEM_STATUS_FIELD_LABELS = {
  unit: "Item Status",
  fulfillment: "Fulfillment Status",
  shipping: "Shipping Status",
} as const satisfies Record<"unit" | "fulfillment" | "shipping", string>;

export const LINE_ITEM_FIELD_LABELS = {
  pets: "Pets",
  variant: "Variant",
  schedule: "Schedule",
  address: "Address",
  location: "Location",
  trackingNumber: "Tracking Number",
  cancelledQuantity: "Cancellation",
} as const;

export const LINE_ITEM_SHIPPING_STATUS_COLORS: Record<OrderLineItemShippingStatus, SPLOOT_BADGE_COLOR> =
  {
    [OrderLineItemShippingStatus.PENDING]: SPLOOT_BADGE_COLOR.ORANGE,
    [OrderLineItemShippingStatus.SHIPPED]: SPLOOT_BADGE_COLOR.BLUE,
    [OrderLineItemShippingStatus.DELIVERED]: SPLOOT_BADGE_COLOR.GREEN,
  };

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  [AdminOrderPaymentStatus.PENDING]: "Pending",
  [AdminOrderPaymentStatus.PARTIALLY_COLLECTED]: "Partially Collected",
  [AdminOrderPaymentStatus.COLLECTED]: "Collected",
  [AdminOrderPaymentStatus.VERIFIED]: "Verified",
  [AdminOrderPaymentStatus.FAILED]: "Failed",
  [AdminOrderPaymentStatus.CANCELLED]: "Cancelled",
};

export const PAYMENT_STATUS_COLORS: Record<string, SPLOOT_BADGE_COLOR> = {
  [AdminOrderPaymentStatus.PENDING]: SPLOOT_BADGE_COLOR.ORANGE,
  [AdminOrderPaymentStatus.PARTIALLY_COLLECTED]: SPLOOT_BADGE_COLOR.YELLOW,
  [AdminOrderPaymentStatus.COLLECTED]: SPLOOT_BADGE_COLOR.GREEN,
  [AdminOrderPaymentStatus.VERIFIED]: SPLOOT_BADGE_COLOR.GREEN,
  [AdminOrderPaymentStatus.FAILED]: SPLOOT_BADGE_COLOR.RED,
  [AdminOrderPaymentStatus.CANCELLED]: SPLOOT_BADGE_COLOR.RED,
};

export const ORDER_PAYMENT_STATUS_LABELS: Record<OrderPaymentStatus, string> = {
  [OrderPaymentStatus.PENDING]: "Pending",
  [OrderPaymentStatus.PARTIALLY_PAID]: "Partially Paid",
  [OrderPaymentStatus.PAID]: "Paid",
};

export const ORDER_PAYMENT_STATUS_COLORS: Record<OrderPaymentStatus, SPLOOT_BADGE_COLOR> = {
  [OrderPaymentStatus.PENDING]: SPLOOT_BADGE_COLOR.ORANGE,
  [OrderPaymentStatus.PARTIALLY_PAID]: SPLOOT_BADGE_COLOR.YELLOW,
  [OrderPaymentStatus.PAID]: SPLOOT_BADGE_COLOR.GREEN,
};

export const PAYMENT_METHOD_LABELS: Record<PaymentMethodType, string> = {
  [PaymentMethodType.CARD]: "Card",
  [PaymentMethodType.UPI]: "UPI",
  [PaymentMethodType.NET_BANKING]: "Net Banking",
  [PaymentMethodType.COD]: "Cash on Delivery",
  [PaymentMethodType.WALLET]: "Wallet",
  [PaymentMethodType.EXTERNAL]: "External",
  [PaymentMethodType.UNKNOWN]: "Unknown",
};

export const PAYMENT_PROVIDER_LABELS: Record<PaymentProvider, string> = {
  [PaymentProvider.JUSPAY]: "Juspay",
  [PaymentProvider.NONE]: "None",
};

export const REFUND_STATUS_LABELS: Record<RefundStatus, string> = {
  [RefundStatus.PENDING]: "Pending",
  [RefundStatus.COMPLETED]: "Completed",
  [RefundStatus.FAILED]: "Failed",
  [RefundStatus.MANUAL_REVIEW]: "Manual Review",
};

export const REFUND_STATUS_COLORS: Record<RefundStatus, SPLOOT_BADGE_COLOR> = {
  [RefundStatus.PENDING]: SPLOOT_BADGE_COLOR.ORANGE,
  [RefundStatus.COMPLETED]: SPLOOT_BADGE_COLOR.GREEN,
  [RefundStatus.FAILED]: SPLOOT_BADGE_COLOR.RED,
  [RefundStatus.MANUAL_REVIEW]: SPLOOT_BADGE_COLOR.YELLOW,
};

export const ORDER_STATUS_FILTER_OPTIONS = Object.entries(ORDER_STATUS_LABELS).map(
  ([value, label]) => ({ value: value as AdminOrderStatus, label }),
);

export const PAYMENT_STATUS_FILTER_OPTIONS = Object.entries(PAYMENT_STATUS_LABELS).map(
  ([value, label]) => ({ value: value as AdminOrderPaymentStatus, label }),
);

export const FULFILLMENT_STATUS_FILTER_OPTIONS = Object.entries(FULFILLMENT_STATUS_LABELS).map(
  ([value, label]) => ({ value: value as AdminOrderFulfillmentStatus, label }),
);